import type { Query } from 'best-modules/utils'
import { sendRequest } from 'best-modules/vue-utils'
import type { PaginationList } from 'best-modules/components/server-table/types'
import type {
  Incident,
  IncidentCreateCredentials,
  IncidentDeleteResponse,
  IncidentDepartment,
  IncidentDepartmentPayload,
  IncidentExecutor,
  IncidentExecutorPayload,
  IncidentIssue,
  IncidentIssueCredentials,
  IncidentResolution,
  IncidentResolutionCredentials,
  IncidentRiskAssessment,
  IncidentRiskAssessmentPayload,
  IncidentStatusPayload,
  IncidentUpdateCredentials,
} from '@/utils/types/incident'

const incidentList = (query: Query): Promise<PaginationList<Incident>> => {
  return sendRequest({
    name: 'incidentList',
    method: 'get',
    url: {
      url: '/json/incident',
      query,
    },
  })
}

const incidentSingle = (incidentId: number): Promise<Incident> => {
  return sendRequest({
    name: 'incidentSingle',
    method: 'get',
    url: `/json/incident/single/${incidentId}`,
  })
}

const incidentCreate = (
  credentials: IncidentCreateCredentials
): Promise<Incident> => {
  return sendRequest({
    name: 'incidentCreate',
    method: 'post',
    url: '/json/incident/create',
    payload: credentials,
  })
}

const incidentUpdate = (
  incidentId: number,
  credentials: IncidentUpdateCredentials
): Promise<Incident> => {
  return sendRequest({
    name: 'incidentUpdate',
    method: 'put',
    url: `/json/incident/update/${incidentId}`,
    payload: credentials,
  })
}

const incidentSetStatus = (
  incidentId: number,
  credentials: IncidentStatusPayload
): Promise<Incident> => {
  return sendRequest({
    name: 'incidentSetStatus',
    method: 'post',
    url: `/json/incident/setStatus/${incidentId}`,
    payload: credentials,
  })
}

const incidentDepartmentList = (
  incidentId: number
): Promise<IncidentDepartment[]> => {
  return sendRequest({
    name: 'incidentDepartmentList',
    method: 'get',
    url: `/json/incident/department/byIncident/${incidentId}`,
  })
}

const incidentDepartmentCreate = (
  credentials: IncidentDepartmentPayload
): Promise<IncidentDepartment> => {
  return sendRequest({
    name: 'incidentDepartmentCreate',
    method: 'post',
    url: '/json/incident/department/create',
    payload: credentials,
  })
}

const incidentDepartmentDelete = (
  departmentId: number
): Promise<IncidentDeleteResponse> => {
  return sendRequest({
    name: 'incidentDepartmentDelete',
    method: 'delete',
    url: `/json/incident/department/delete/${departmentId}`,
  })
}

const incidentExecutorList = (
  incidentId: number
): Promise<IncidentExecutor[]> => {
  return sendRequest({
    name: 'incidentExecutorList',
    method: 'get',
    url: `/json/incident/executor/byIncident/${incidentId}`,
  })
}

const incidentExecutorCreate = (
  credentials: IncidentExecutorPayload
): Promise<IncidentExecutor> => {
  return sendRequest({
    name: 'incidentExecutorCreate',
    method: 'post',
    url: '/json/incident/executor/create',
    payload: credentials,
  })
}

const incidentExecutorDelete = (
  executorId: number
): Promise<IncidentDeleteResponse> => {
  return sendRequest({
    name: 'incidentExecutorDelete',
    method: 'delete',
    url: `/json/incident/executor/delete/${executorId}`,
  })
}

const incidentRiskAssessmentList = (
  incidentId: number
): Promise<IncidentRiskAssessment[]> => {
  return sendRequest({
    name: 'incidentRiskAssessmentList',
    method: 'get',
    url: `/json/incident/riskAssessment/byIncident/${incidentId}`,
  })
}

const incidentRiskAssessmentCreate = (
  credentials: IncidentRiskAssessmentPayload
): Promise<IncidentRiskAssessment> => {
  return sendRequest({
    name: 'incidentRiskAssessmentCreate',
    method: 'post',
    url: '/json/incident/riskAssessment/create',
    payload: credentials,
  })
}

const incidentRiskAssessmentUpdate = (
  riskAssessmentId: number,
  credentials: IncidentRiskAssessmentPayload
): Promise<IncidentRiskAssessment> => {
  return sendRequest({
    name: 'incidentRiskAssessmentUpdate',
    method: 'put',
    url: `/json/incident/riskAssessment/update/${riskAssessmentId}`,
    payload: credentials,
  })
}

const incidentRiskAssessmentDelete = (
  riskAssessmentId: number
): Promise<IncidentDeleteResponse> => {
  return sendRequest({
    name: 'incidentRiskAssessmentDelete',
    method: 'delete',
    url: `/json/incident/riskAssessment/delete/${riskAssessmentId}`,
  })
}

const incidentIssueList = (incidentId: number): Promise<IncidentIssue[]> => {
  return sendRequest({
    name: 'incidentIssueList',
    method: 'get',
    url: `/json/incident/issue/byIncident/${incidentId}`,
  })
}

const incidentIssueCreate = (
  credentials: IncidentIssueCredentials
): Promise<IncidentIssue> => {
  return sendRequest({
    name: 'incidentIssueCreate',
    method: 'post',
    url: '/json/incident/issue/create',
    payload: credentials,
  })
}

const incidentIssueUpdate = (
  credentials: { topic: string; description: string },
  issueId: number
): Promise<IncidentIssue> => {
  return sendRequest({
    name: 'incidentIssueUpdate',
    method: 'put',
    url: `/json/incident/issue/update/${issueId}`,
    payload: credentials,
  })
}

const incidentIssueDelete = (issueId: number): Promise<IncidentIssue> => {
  return sendRequest({
    name: 'incidentIssueDelete',
    method: 'delete',
    url: `/json/incident/issue/delete/${issueId}`,
  })
}

const incidentIssueSetStatus = (
  issueId: number,
  credentials: { statusId: number }
): Promise<IncidentIssue> => {
  return sendRequest({
    name: 'incidentIssueSetStatus',
    method: 'post',
    url: `/json/incident/issue/setStatus/${issueId}`,
    payload: credentials,
  })
}

const incidentResolutionCreate = (
  credentials: IncidentResolutionCredentials
): Promise<IncidentResolution> => {
  return sendRequest({
    name: 'incidentResolutionCreate',
    method: 'post',
    url: '/json/incident/resolution/create',
    payload: credentials,
  })
}

export {
  incidentCreate,
  incidentDepartmentCreate,
  incidentDepartmentDelete,
  incidentDepartmentList,
  incidentExecutorCreate,
  incidentExecutorDelete,
  incidentExecutorList,
  incidentIssueCreate,
  incidentIssueUpdate,
  incidentIssueDelete,
  incidentIssueList,
  incidentIssueSetStatus,
  incidentList,
  incidentRiskAssessmentCreate,
  incidentRiskAssessmentDelete,
  incidentRiskAssessmentList,
  incidentRiskAssessmentUpdate,
  incidentResolutionCreate,
  incidentSetStatus,
  incidentSingle,
  incidentUpdate,
}
